import { extractAlxDataFromText } from '../alx/extractFromTxt'
import type { UploadFormat } from './types'

export type WeldTxtFileFormat = UploadFormat | 'unknown'

export type WeldTxtBatchClassification =
  | { kind: 'all-same'; format: UploadFormat }
  | { kind: 'mixed' }
  | { kind: 'all-unknown' }

function normalizeHeader(value: string): string {
  return String(value || '')
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .trim()
}

/**
 * Lightweight telemetry header check — same heuristic as `parseWeldTxt` uses
 * before parsing data rows (tab-separated header with machine export columns).
 */
export function looksLikeTelemetryHeader(text: string): boolean {
  const rawLines = text.split(/\r?\n/).map((line) => line.replace(/\r$/, ''))
  const headerLine = rawLines.find((line) => line.trim().length > 0)
  if (!headerLine || !headerLine.includes('\t')) return false

  const headerColumns = headerLine.split('\t').map(normalizeHeader)
  if (headerColumns.every((h) => h === '')) return false

  const headerSet = new Set(headerColumns.filter((h) => h.length > 0))
  const hasEvt = ['evt', 'evt_no', 'evt_number'].some((a) => headerSet.has(a))
  const hasWeld = ['weld', 'weld_no', 'weld_number'].some((a) => headerSet.has(a))
  const hasVolts = ['ld_volts', 'tr_volts'].some((a) => headerSet.has(a))
  return hasEvt && hasWeld && hasVolts
}

export function classifyWeldTxtFile(text: string): WeldTxtFileFormat {
  if (extractAlxDataFromText(text) !== null) {
    return 'alx-summary'
  }
  if (looksLikeTelemetryHeader(text)) {
    return 'telemetry-txt'
  }
  return 'unknown'
}

export interface ClassifiedFile {
  name: string
  format: WeldTxtFileFormat
}

export function classifyWeldTxtBatch(
  files: Array<{ name: string; text: string }>
): WeldTxtBatchClassification & { perFile: ClassifiedFile[] } {
  const perFile = files.map((f) => ({
    name: f.name,
    format: classifyWeldTxtFile(f.text)
  }))

  if (perFile.length === 0) {
    return { kind: 'all-unknown', perFile }
  }

  const formats = new Set(perFile.map((f) => f.format))
  const known = perFile.filter((f) => f.format !== 'unknown')

  if (known.length === 0) {
    return { kind: 'all-unknown', perFile }
  }

  const knownFormats = new Set(known.map((f) => f.format))
  if (knownFormats.size > 1) {
    return { kind: 'mixed', perFile }
  }

  if (perFile.some((f) => f.format === 'unknown')) {
    return { kind: 'mixed', perFile }
  }

  return { kind: 'all-same', format: known[0].format as UploadFormat, perFile }
}

export const MIXED_FORMAT_ERROR =
  'Cannot mix ALX summary logs and machine telemetry files in one upload.'

export const UNKNOWN_FORMAT_ERROR =
  'No valid welding log files detected. Upload ALX summary .txt files or machine telemetry .txt exports.'

export function formatMismatchError(existing: UploadFormat, detected: UploadFormat): string {
  const existingLabel = existing === 'alx-summary' ? 'ALX summary' : 'machine telemetry'
  const detectedLabel = detected === 'alx-summary' ? 'ALX summary' : 'machine telemetry'
  return (
    `This weld already has ${existingLabel} parameters. ` +
    `Delete the existing data on the Parameters page before uploading ${detectedLabel} files.`
  )
}
